import { useRef, useState } from 'react';
import usePostJobSources from '../api/usePostJobSources';
import useFetchJobSource from '../api/useFetchJobSource';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import type { JobSourceServiceType } from '@/types/serializers';

// 作成直後に取り込み完了までポーリングして待つソース
const POLLING_SOURCE_TYPES = ['z_career', 'circus_agent', 'crowd_agent', 'jobins'];

export type CredentialCreateInput = {
  sourceType: string;
  loginId: string;
  loginPass: string;
  accessUrl?: string;
  basicAuthId?: string;
  basicAuthPass?: string;
};

export type UrlCreateInput = {
  sourceType: string;
  urls: string[];
  loginId?: string;
  loginPass?: string;
};

export type CreateResult = {
  status: 'success' | 'polling' | 'error';
  serviceType?: JobSourceServiceType;
};

export function isPollingSourceType(sourceType: string | undefined): boolean {
  return !!sourceType && POLLING_SOURCE_TYPES.includes(sourceType);
}

function toCredentialErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'ログインに失敗しました';
  if (error.message === 'このアカウントは既に登録されています') return error.message;
  // 求人ソースの登録上限（シングルプラン）に達した場合はサーバーのメッセージをそのまま表示する
  if (error.message.includes('上限')) return error.message;
  if (error.message.includes('401') || error.message.includes('認証')) {
    return 'ログインIDまたはパスワードが正しくありません';
  }
  if (error.message.includes('network') || error.message.includes('fetch')) {
    return 'ネットワークエラーが発生しました。しばらく時間をおいて再度お試しください';
  }
  return 'ログインに失敗しました';
}

export default function useCreateJobSource() {
  const { mutateAsync, isPending } = usePostJobSources();
  const [pollingSourceType, setPollingSourceType] = useState<string | null>(null);
  const [createdJobSourceId, setCreatedJobSourceId] = useState<number | null>(null);
  // isPending の disabled 反映は再レンダリング後のため、ダブルクリックの同時 POST は ref で同期的に弾く
  const inFlightRef = useRef(false);

  // 取り込み完了後も「同期中」画面（LinkedAccountLoading）に留まる現行挙動を維持するため完了コールバックは持たない
  useFetchJobSource(
    createdJobSourceId,
    pollingSourceType
      ? {
          pollUntilCollectionComplete: true,
          onLoginFailed: () => {
            showErrorToast('ログインに失敗しました');
            setPollingSourceType(null);
            setCreatedJobSourceId(null);
          },
        }
      : undefined
  );

  const createWithCredentials = async (input: CredentialCreateInput): Promise<CreateResult> => {
    if (inFlightRef.current) return { status: 'error' };
    inFlightRef.current = true;
    try {
      const data = await mutateAsync({
        source_type: input.sourceType,
        login_id: input.loginId,
        login_pass: input.loginPass,
        ...(input.accessUrl ? { access_url: input.accessUrl } : {}),
        ...(input.basicAuthId ? { basic_auth_id: input.basicAuthId } : {}),
        ...(input.basicAuthPass ? { basic_auth_pass: input.basicAuthPass } : {}),
      });

      if (isPollingSourceType(input.sourceType) && data?.id) {
        setCreatedJobSourceId(data.id);
        setPollingSourceType(input.sourceType);
        return { status: 'polling', serviceType: data.service_type };
      }

      showSuccessToast('求人の取り込みを開始しました');
      return { status: 'success', serviceType: data.service_type };
    } catch (error) {
      showErrorToast(toCredentialErrorMessage(error));
      setPollingSourceType(null);
      setCreatedJobSourceId(null);
      return { status: 'error' };
    } finally {
      inFlightRef.current = false;
    }
  };

  const createWithUrls = async (input: UrlCreateInput): Promise<CreateResult> => {
    if (inFlightRef.current) return { status: 'error' };
    inFlightRef.current = true;
    try {
      const data = await mutateAsync({
        source_type: input.sourceType,
        urls: input.urls,
        ...(input.loginId && input.loginPass
          ? { login_id: input.loginId, login_pass: input.loginPass }
          : {}),
      });
      showSuccessToast('求人の取り込みを開始しました');
      return { status: 'success', serviceType: data.service_type };
    } catch (error) {
      // 求人ソースの登録上限（シングルプラン）に達した場合はサーバーのメッセージをそのまま表示する
      const message =
        error instanceof Error && error.message.includes('上限')
          ? error.message
          : '求人の取り込みに失敗しました';
      showErrorToast(message);
      return { status: 'error' };
    } finally {
      inFlightRef.current = false;
    }
  };

  return {
    createWithCredentials,
    createWithUrls,
    isPending,
    isPolling: pollingSourceType !== null,
    pollingSourceType,
  };
}
